import { useState, useMemo } from 'react';
import { X, Plus, Trash2, Calculator, TrendingUp, Building2, HandCoins, Wallet, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { InvestmentWithStats } from '../../hooks/useInvestments';
import { useFinancingPlan, FinancingLine } from '../../hooks/useFinancingPlan';

interface FinancingPlanModalProps {
  investments: InvestmentWithStats[];
  onClose: () => void;
}

type NewLineForm = {
  type: FinancingLine['type'];
  label: string;
  amount: number | '';
  rate: number | '';
  duration_years: number | '';
  start_date: string;
  notes: string;
};

function computeMonthlyPayment(principal: number, annualRate: number, durationYears: number): number {
  if (annualRate === 0) return principal / (durationYears * 12);
  const r = annualRate / 100 / 12;
  const n = durationYears * 12;
  return principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

function computeAmortizationSchedule(principal: number, annualRate: number, durationYears: number) {
  const monthly = computeMonthlyPayment(principal, annualRate, durationYears);
  const r = annualRate / 100 / 12;
  const n = durationYears * 12;
  let balance = principal;
  const rows = [];
  for (let i = 1; i <= n; i++) {
    const interest = balance * r;
    const capital = monthly - interest;
    balance -= capital;
    rows.push({ period: i, payment: monthly, interest, capital, balance: Math.max(0, balance) });
  }
  return { monthly, rows, totalInterest: monthly * n - principal };
}

const TYPE_COLORS: Record<FinancingLine['type'], string> = {
  'Prêt bancaire': 'bg-blue-100 text-blue-700 border-blue-200',
  'Subvention': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Autofinancement': 'bg-gray-100 text-gray-700 border-gray-200',
  'Autre': 'bg-orange-100 text-orange-700 border-orange-200'
};

const TYPE_ICONS: Record<FinancingLine['type'], React.ReactNode> = {
  'Prêt bancaire': <Building2 size={16} />,
  'Subvention': <HandCoins size={16} />,
  'Autofinancement': <Wallet size={16} />,
  'Autre': <TrendingUp size={16} />
};

const DEFAULT_FORM: NewLineForm = {
  type: 'Prêt bancaire',
  label: '',
  amount: '',
  rate: 3.5,
  duration_years: 7,
  start_date: new Date().toISOString().split('T')[0],
  notes: ''
};

export function FinancingPlanModal({ investments, onClose }: FinancingPlanModalProps) {
  const { lines, loading, addLine, removeLine } = useFinancingPlan();
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedLoan, setExpandedLoan] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [newLine, setNewLine] = useState<NewLineForm>(DEFAULT_FORM);

  const activeInvestments = investments.filter(i => i.status !== 'Cancelled');
  const totalInvestments = activeInvestments.reduce((sum, i) => sum + i.total_budget, 0);
  const totalFinancing = lines.reduce((sum, l) => sum + l.amount, 0);
  const balance = totalFinancing - totalInvestments;
  const coverageRate = totalInvestments > 0 ? (totalFinancing / totalInvestments) * 100 : 0;

  const totalLoanCost = useMemo(() => {
    return lines
      .filter(l => l.type === 'Prêt bancaire' && l.rate && l.duration_years)
      .reduce((sum, l) => {
        const { totalInterest } = computeAmortizationSchedule(l.amount, l.rate!, l.duration_years!);
        return sum + totalInterest;
      }, 0);
  }, [lines]);

  const fmt = (n: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

  const handleAdd = async () => {
    if (!newLine.label || !newLine.amount || newLine.amount <= 0) return;
    setSaving(true);
    try {
      await addLine({
        type: newLine.type,
        label: newLine.label,
        amount: Number(newLine.amount),
        rate: newLine.type === 'Prêt bancaire' && newLine.rate !== '' ? Number(newLine.rate) : undefined,
        duration_years: newLine.type === 'Prêt bancaire' && newLine.duration_years !== '' ? Number(newLine.duration_years) : undefined,
        start_date: newLine.start_date || undefined,
        notes: newLine.notes || undefined,
        sort_order: lines.length
      });
      setNewLine(DEFAULT_FORM);
      setShowAddForm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id: string) => {
    await removeLine(id);
    if (expandedLoan === id) setExpandedLoan(null);
  };

  const previewMonthly = newLine.type === 'Prêt bancaire' && newLine.amount && newLine.rate !== '' && newLine.duration_years !== ''
    ? computeMonthlyPayment(Number(newLine.amount), Number(newLine.rate), Number(newLine.duration_years))
    : null;
  const previewInterest = previewMonthly && newLine.duration_years !== ''
    ? computeAmortizationSchedule(Number(newLine.amount), Number(newLine.rate), Number(newLine.duration_years)).totalInterest
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10 rounded-t-xl">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Plan de Financement</h2>
            <p className="text-sm text-gray-500 mt-0.5">Montage financier et simulation de crédits</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Besoins en financement</h3>
              <div className="space-y-2">
                {activeInvestments.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">Aucun investissement actif</p>
                ) : (
                  activeInvestments.map(inv => (
                    <div key={inv.id} className="flex justify-between items-center text-sm">
                      <span className="text-gray-700 truncate flex-1 mr-2">{inv.name}</span>
                      <span className="font-medium text-gray-900 shrink-0">{fmt(inv.total_budget)}</span>
                    </div>
                  ))
                )}
                <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between items-center">
                  <span className="font-semibold text-gray-900 text-sm">Total</span>
                  <span className="font-bold text-gray-900">{fmt(totalInvestments)}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className={`rounded-lg border p-4 ${balance >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {balance >= 0 ? 'Excédent de financement' : 'Déficit de financement'}
                </div>
                <div className={`text-2xl font-bold ${balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {balance >= 0 ? '+' : ''}{fmt(balance)}
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-gray-500 font-medium">Taux de couverture</span>
                  <span className={`text-sm font-bold ${coverageRate >= 100 ? 'text-emerald-600' : coverageRate >= 80 ? 'text-orange-600' : 'text-red-600'}`}>
                    {coverageRate.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full transition-all ${coverageRate >= 100 ? 'bg-emerald-500' : coverageRate >= 80 ? 'bg-orange-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.min(100, coverageRate)}%` }}
                  />
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  {fmt(totalFinancing)} financés sur {fmt(totalInvestments)}
                </div>
              </div>

              {totalLoanCost > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Calculator size={14} className="text-blue-600" />
                    <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Coût total des intérêts</span>
                  </div>
                  <div className="text-xl font-bold text-blue-800">{fmt(totalLoanCost)}</div>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Sources de financement</h3>
              {!showAddForm && (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <Plus size={16} />
                  Ajouter
                </button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-8 text-gray-400 text-sm">Chargement...</div>
            ) : lines.length === 0 && !showAddForm ? (
              <div className="text-center py-10 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                <Calculator size={32} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">Aucune source de financement ajoutée</p>
                <p className="text-xs mt-1">Ajoutez un prêt bancaire, une subvention, de l'autofinancement...</p>
              </div>
            ) : null}

            <div className="space-y-3">
              {lines.map(line => {
                const isLoan = line.type === 'Prêt bancaire' && line.rate && line.duration_years;
                const isExpanded = expandedLoan === line.id;
                const schedule = isLoan ? computeAmortizationSchedule(line.amount, line.rate!, line.duration_years!) : null;

                return (
                  <div key={line.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="flex items-center gap-3 p-4 bg-white">
                      <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border shrink-0 ${TYPE_COLORS[line.type]}`}>
                        {TYPE_ICONS[line.type]}
                        {line.type}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 text-sm">{line.label}</div>
                        {isLoan && schedule && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {line.rate}% sur {line.duration_years} ans &bull; {fmt(schedule.monthly)}/mois &bull; Intérêts : {fmt(schedule.totalInterest)}
                          </div>
                        )}
                        {line.notes && <div className="text-xs text-gray-400 mt-0.5 italic">{line.notes}</div>}
                      </div>
                      <div className="font-bold text-gray-900 text-sm shrink-0">{fmt(line.amount)}</div>
                      <div className="flex items-center gap-1">
                        {isLoan && (
                          <button
                            onClick={() => setExpandedLoan(isExpanded ? null : line.id)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors rounded"
                            title="Tableau d'amortissement"
                          >
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        )}
                        <button
                          onClick={() => handleRemove(line.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-colors rounded"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    {isExpanded && schedule && (
                      <div className="border-t border-gray-100 bg-gray-50 p-4">
                        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                          Tableau d'amortissement &bull; {line.duration_years! * 12} mensualités de {fmt(schedule.monthly)}
                        </h4>
                        <div className="max-h-56 overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-gray-100">
                              <tr>
                                <th className="px-2 py-1.5 text-left text-gray-600 font-medium">Échéance</th>
                                <th className="px-2 py-1.5 text-right text-gray-600 font-medium">Mensualité</th>
                                <th className="px-2 py-1.5 text-right text-gray-600 font-medium">Capital</th>
                                <th className="px-2 py-1.5 text-right text-gray-600 font-medium">Intérêts</th>
                                <th className="px-2 py-1.5 text-right text-gray-600 font-medium">CRD</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {schedule.rows.map(row => (
                                <tr key={row.period} className="bg-white hover:bg-blue-50 transition-colors">
                                  <td className="px-2 py-1.5 text-gray-700">M{row.period}</td>
                                  <td className="px-2 py-1.5 text-right font-medium">{fmt(row.payment)}</td>
                                  <td className="px-2 py-1.5 text-right text-emerald-600">{fmt(row.capital)}</td>
                                  <td className="px-2 py-1.5 text-right text-red-500">{fmt(row.interest)}</td>
                                  <td className="px-2 py-1.5 text-right text-gray-600">{fmt(row.balance)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {showAddForm && (
                <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50 space-y-4">
                  <h4 className="font-semibold text-gray-800 text-sm">Nouvelle source de financement</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                      <select
                        value={newLine.type}
                        onChange={e => setNewLine({ ...newLine, type: e.target.value as FinancingLine['type'] })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      >
                        <option value="Prêt bancaire">Prêt bancaire</option>
                        <option value="Subvention">Subvention</option>
                        <option value="Autofinancement">Autofinancement</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Intitulé *</label>
                      <input
                        type="text"
                        value={newLine.label}
                        onChange={e => setNewLine({ ...newLine, label: e.target.value })}
                        placeholder="Ex: Crédit BPI, Subvention Région..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Montant (€) *</label>
                      <input
                        type="number"
                        step="100"
                        min="0"
                        value={newLine.amount}
                        onChange={e => setNewLine({ ...newLine, amount: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Date de départ</label>
                      <input
                        type="date"
                        value={newLine.start_date}
                        onChange={e => setNewLine({ ...newLine, start_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      />
                    </div>
                  </div>

                  {newLine.type === 'Prêt bancaire' && (
                    <div className="space-y-3 p-3 bg-white rounded-lg border border-blue-200">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Taux annuel (%)</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="30"
                            value={newLine.rate}
                            onChange={e => setNewLine({ ...newLine, rate: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Durée (années)</label>
                          <input
                            type="number"
                            step="1"
                            min="1"
                            max="30"
                            value={newLine.duration_years}
                            onChange={e => setNewLine({ ...newLine, duration_years: e.target.value === '' ? '' : parseInt(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      </div>
                      {previewMonthly !== null && previewInterest !== null && (
                        <div className="bg-blue-50 rounded p-2 text-xs text-blue-800 flex items-center gap-2">
                          <Calculator size={13} />
                          Mensualité estimée : <strong>{fmt(previewMonthly)}</strong>
                          &nbsp;&bull;&nbsp;Coût total des intérêts : <strong>{fmt(previewInterest)}</strong>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                    <input
                      type="text"
                      value={newLine.notes}
                      onChange={e => setNewLine({ ...newLine, notes: e.target.value })}
                      placeholder="Observations, conditions..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    />
                  </div>

                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => { setShowAddForm(false); setNewLine(DEFAULT_FORM); }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors text-sm"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleAdd}
                      disabled={!newLine.label || !newLine.amount || newLine.amount <= 0 || saving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Sauvegarde...' : 'Ajouter'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {lines.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Récapitulatif du montage</h3>
              <div className="space-y-1.5">
                {(['Prêt bancaire', 'Subvention', 'Autofinancement', 'Autre'] as FinancingLine['type'][]).map(type => {
                  const typeLines = lines.filter(l => l.type === type);
                  if (typeLines.length === 0) return null;
                  const typeTotal = typeLines.reduce((s, l) => s + l.amount, 0);
                  const pct = totalFinancing > 0 ? (typeTotal / totalFinancing) * 100 : 0;
                  return (
                    <div key={type} className="flex items-center gap-3">
                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border shrink-0 ${TYPE_COLORS[type]}`}>
                        {TYPE_ICONS[type]}
                        {type}
                      </span>
                      <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${type === 'Prêt bancaire' ? 'bg-blue-500' : type === 'Subvention' ? 'bg-emerald-500' : type === 'Autofinancement' ? 'bg-gray-500' : 'bg-orange-500'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 shrink-0 w-10 text-right">{pct.toFixed(0)}%</span>
                      <span className="text-sm font-medium text-gray-900 shrink-0 w-24 text-right">{fmt(typeTotal)}</span>
                    </div>
                  );
                })}
                <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between">
                  <span className="font-semibold text-sm text-gray-900">Total financement</span>
                  <span className="font-bold text-sm text-gray-900">{fmt(totalFinancing)}</span>
                </div>
                {totalLoanCost > 0 && (
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Dont intérêts bancaires</span>
                    <span className="text-red-500">{fmt(totalLoanCost)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {balance < 0 && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <strong>Déficit de {fmt(Math.abs(balance))}</strong> — Le plan de financement ne couvre pas l'ensemble des investissements prévus. Ajoutez des sources de financement complémentaires.
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end rounded-b-xl">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors text-sm font-medium"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
